import React from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Grid } from '@mui/material';
import { FOR_BANK_SECTIONS, FOR_BANK_SUB_SECTIONS } from 'constants/businessToolSections/forBank';

import useCheckSubSectionAccess from 'hooks/useCheckSubSectionAccess';

import { FieldArrayComponent } from 'components/_basic/Form/FieldArray';
import { TextInputControl } from 'components/_basic/Form/TextInput';
import { FormHeader } from 'components/_basic/FormHeader/FormHeader';
import { FormSectionCard } from 'components/_basic/FormSectionCard/FormSectionCard';
import { FORM_SECTION_CARD_VARIANT } from 'components/_basic/FormSectionCard/types';
import { NumericBox } from 'components/BusinessToolForm/components/NumericBox';

import { concatWithDot } from 'helpers/string';

import { prefix, STAGES_NAMES, stagesValues, TECHNOLOGICAL_PROCESS_NAMES } from './utils/form';

import styles from './TechnologicalProcess.module.scss';

export const TechnologicalProcess: React.FC = () => {
  const { t } = useTranslation();
  const { control } = useFormContext();

  const canAccess = useCheckSubSectionAccess(
    FOR_BANK_SECTIONS.BUSINESS_OPERATIONS,
    FOR_BANK_SUB_SECTIONS.FOR_BANK_BUSINESS_OPERATIONS.TECHNOLOGICAL_PROCESS,
  );

  return (
    <>
      <FormSectionCard variant={FORM_SECTION_CARD_VARIANT.LARGE}>
        <FormHeader
          locked={!canAccess}
          title={t('businessOperations.title')}
          badgeText={t('businessOperations.technologicalProcess.title')}
          section={FOR_BANK_SECTIONS.BUSINESS_OPERATIONS}
        />
      </FormSectionCard>
      {/* 6.4.1 */}
      <FormSectionCard
        disabled={!canAccess}
        title={t('businessOperations.technologicalProcess.technology')}
        aiFieldName={concatWithDot(prefix, TECHNOLOGICAL_PROCESS_NAMES.TECHNOLOGY)}
      >
        <TextInputControl
          multiline
          control={control}
          id={concatWithDot(prefix, TECHNOLOGICAL_PROCESS_NAMES.TECHNOLOGY)}
          name={concatWithDot(prefix, TECHNOLOGICAL_PROCESS_NAMES.TECHNOLOGY)}
          minRows={4}
          disabled={!canAccess}
          maxLength={1500}
        />
      </FormSectionCard>
      {/* 6.4.2 */}
      <FormSectionCard
        disabled={!canAccess}
        title={t('businessOperations.technologicalProcess.qualities')}
        aiFieldName={concatWithDot(prefix, TECHNOLOGICAL_PROCESS_NAMES.QUALITIES)}
      >
        <TextInputControl
          multiline
          control={control}
          id={concatWithDot(prefix, TECHNOLOGICAL_PROCESS_NAMES.QUALITIES)}
          name={concatWithDot(prefix, TECHNOLOGICAL_PROCESS_NAMES.QUALITIES)}
          minRows={4}
          disabled={!canAccess}
          maxLength={1500}
        />
      </FormSectionCard>
      {/* 6.4.3 */}
      <FormSectionCard
        disabled={!canAccess}
        title={t('businessOperations.technologicalProcess.stages.title')}
        collapsible
      >
        <FieldArrayComponent
          name={concatWithDot(prefix, TECHNOLOGICAL_PROCESS_NAMES.STAGES)}
          initialItem={stagesValues}
          buttonText={t('businessOperations.technologicalProcess.stages.stage')}
          fieldArrayStyles={{ removeButton: styles.removeButton }}
          disabled={!canAccess}
        >
          {(namePrefix, index) => (
            <Grid spacing={1} container>
              <Grid item xs="auto">
                <NumericBox number={index + 1} variant="outlined" sx={{ marginTop: 2.5 }} />
              </Grid>
              <Grid item xs>
                <TextInputControl
                  multiline
                  control={control}
                  id={concatWithDot(namePrefix, STAGES_NAMES.STAGE)}
                  name={concatWithDot(namePrefix, STAGES_NAMES.STAGE)}
                  label={t('businessOperations.technologicalProcess.stages.stage')}
                  disabled={!canAccess}
                  maxLength={150}
                />
              </Grid>
            </Grid>
          )}
        </FieldArrayComponent>
      </FormSectionCard>
      {/* 6.4.4 */}
      <FormSectionCard
        disabled={!canAccess}
        title={t('businessOperations.technologicalProcess.competingTechnology')}
        aiFieldName={concatWithDot(prefix, TECHNOLOGICAL_PROCESS_NAMES.COMPETING_TECHNOLOGY)}
      >
        <TextInputControl
          multiline
          control={control}
          id={concatWithDot(prefix, TECHNOLOGICAL_PROCESS_NAMES.COMPETING_TECHNOLOGY)}
          name={concatWithDot(prefix, TECHNOLOGICAL_PROCESS_NAMES.COMPETING_TECHNOLOGY)}
          minRows={4}
          disabled={!canAccess}
          maxLength={1500}
        />
      </FormSectionCard>
    </>
  );
};
